import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { useEffect, useState } from "react";

import { useUpdateEdiFileMappingMutation } from "../../../store/queries/ediConversion";
import {
  EdiClaimType,
  IEdiConversionFile,
  IEdiUploadMapping,
} from "../../../types/ediConversionFile";
import { IProvider } from "../../../types/Provider/Provider";
import { validateEdiMappingSet } from "./ediMappingValidation";
import MappingRuleFields, { posFilterAppliesTo } from "./MappingRuleFields";

// Seed a mapping from an existing file row (its providerId / claimType columns + metadata).
const mappingFromFile = (file: IEdiConversionFile): IEdiUploadMapping => {
  const md = file.metadata ?? {};
  return {
    // Legacy single-provider rows have a null claimType; default them to Professional — Provider so
    // the form has a concrete value (the backend applies the same routing for a null claimType row).
    claimType: (file.claimType ?? "PROFESSIONAL_PROVIDER") as EdiClaimType,
    providerId: file.providerId ?? "",
    placeOfServiceFilter: md.placeOfServiceFilter ?? [],
    excludeMidlevels: md.excludeMidlevels ?? false,
    excludeBehavioralHealth: md.excludeBehavioralHealth ?? false,
    payerTaxonomyRules: md.payerTaxonomyRules ?? [],
  };
};

interface IEditMappingModalProps {
  file: IEdiConversionFile | null;
  visible: boolean;
  onHide: () => void;
  providers: IProvider[];
  payerOptions: { value: number; label: string }[];
  taxonomyOptions: { value: string; label: string }[];
  // The mapping rules of the OTHER rows in this file's upload batch (CLE-2424 cardinality is enforced
  // across the whole batch). Used to disable already-used claim types and to validate the edited set.
  siblingMappings: IEdiUploadMapping[];
  onSaved: (fileId: string, displayName: string) => void;
  onError: (message: string) => void;
}

// CLE-2479 — edit the provider-mapping of an already-processed EDI file. Reuses the same routing
// controls as upload (claim type → provider, place of service, taxonomy filter) for a single row,
// via the shared MappingRuleFields editor. Saving persists the mapping only; the caller prompts to
// reprocess so it's applied to claims.
function EditMappingModal({
  file,
  visible,
  onHide,
  providers,
  payerOptions,
  taxonomyOptions,
  siblingMappings,
  onSaved,
  onError,
}: IEditMappingModalProps) {
  const [mapping, setMapping] = useState<IEdiUploadMapping | null>(null);
  const [updateMapping, { isLoading: saving }] =
    useUpdateEdiFileMappingMutation();

  // Reseed whenever a different file is opened.
  useEffect(() => {
    setMapping(file ? mappingFromFile(file) : null);
  }, [file]);

  if (!mapping) return null;

  const showPos = posFilterAppliesTo(mapping.claimType);

  const mappingSetError = validateEdiMappingSet([...siblingMappings, mapping]);
  const canSave = !!mapping.providerId && !mappingSetError && !saving;

  const handleSave = async () => {
    if (!file || !canSave) return;
    // The POS + taxonomy sub-filters only apply to the generic-provider rule; clear them otherwise
    // (the backend enforces this too).
    const normalized: IEdiUploadMapping = showPos
      ? mapping
      : {
          ...mapping,
          placeOfServiceFilter: [],
          excludeMidlevels: undefined,
          excludeBehavioralHealth: undefined,
          payerTaxonomyRules: [],
        };
    try {
      await updateMapping({ id: file.id, mapping: normalized }).unwrap();
      onSaved(file.id, file.displayName);
      onHide();
    } catch (error: any) {
      onError(error?.data?.message || "Failed to update mapping");
    }
  };

  const footer = (
    <div>
      <Button
        label="Cancel"
        className="p-button-text"
        onClick={onHide}
        disabled={saving}
      />
      <Button
        label={saving ? "Saving…" : "Save mapping"}
        icon={saving ? "pi pi-spin pi-spinner" : "pi pi-check"}
        onClick={handleSave}
        disabled={!canSave}
      />
    </div>
  );

  return (
    <Dialog
      header={file ? `Edit mapping — ${file.displayName}` : "Edit mapping"}
      visible={visible}
      onHide={() => {
        if (!saving) onHide();
      }}
      footer={footer}
      style={{ width: "min(760px, 95vw)" }}
      // Roomy content area so the dropdown/multiselect panels have space to open below their inputs
      // instead of feeling cramped against the footer.
      contentStyle={{ minHeight: "min(55vh, 360px)" }}
      modal
      closable={!saving}
      blockScroll
    >
      <small
        className="text-muted"
        style={{ display: "block", marginBottom: "1rem" }}
      >
        Change which provider (and, for professional claims, place of service
        and specialty filter) this file&apos;s claims route to. Saving updates
        the mapping only — the existing claims keep their current provider until
        you reprocess the file.
      </small>

      <MappingRuleFields
        mapping={mapping}
        onChange={setMapping}
        providers={providers}
        disabled={saving}
        // PROFESSIONAL_PROVIDER may repeat (POS-split, CLE-2424); the other types map once per batch.
        disabledClaimTypes={siblingMappings.map((s) => s.claimType)}
        idPrefix="edit"
        payerOptions={payerOptions}
        taxonomyOptions={taxonomyOptions}
      />

      {mappingSetError && (
        <small
          className="p-error"
          style={{ display: "block", marginTop: "0.75rem" }}
        >
          {mappingSetError}
        </small>
      )}
    </Dialog>
  );
}

export default EditMappingModal;
